import type React from 'react';
import { forwardRef } from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Slide,
} from '@mui/material';
import type { DialogProps } from '@mui/material/Dialog';
import type { TransitionProps } from '@mui/material/transitions';

import { PrimaryButton, TransparentButton, DangerButton } from 'app/shared-components/buttons';

type ActionType = 'transparent' | 'primary' | 'danger';

export type Action = {
  title: string;
  cb: (e: React.MouseEvent<HTMLButtonElement>) => void;
  type?: ActionType;
  loading?: boolean;
  disabled?: boolean;
  outlined?: boolean;
};

type DialogModalProps = {
  open: boolean;
  handleClose: DialogProps['onClose'];
  title: string;
  content?: string;
  actions: Action[];
  children?: React.ReactNode;
};

const buttonsByActionType: Record<ActionType, React.ComponentType<any>> = {
  transparent: TransparentButton,
  primary: PrimaryButton,
  danger: DangerButton,
};

const Transition = forwardRef(function Transition(
  props: TransitionProps & { children: React.ReactElement<any, any> },
  ref: React.Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

export default function AlertDialogSlide({
  open,
  handleClose,
  title,
  content,
  actions,
  children,
}: DialogModalProps): React.ReactElement {
  return (
    <Dialog
      open={open}
      TransitionComponent={Transition}
      keepMounted
      onClose={handleClose}
      aria-describedby="alert-dialog-slide-description"
    >
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <DialogContentText id="alert-dialog-slide-description" component="div" className="text-14">
          {content || children}
        </DialogContentText>
      </DialogContent>
      <DialogActions sx={{ padding: '10px 20px' }}>
        {actions.map((action) => {
          const Button = buttonsByActionType[action.type ?? 'primary'] || PrimaryButton;

          return (
            <Button
              key={action.title}
              label={action.title}
              cb={action.cb}
              loading={action.loading}
              disabled={action.disabled}
              outlined={action.outlined}
            />
          );
        })}
      </DialogActions>
    </Dialog>
  );
}
